import React, { useState, useEffect, useRef } from "react";
import FeatherIcon from "feather-icons-react";
import { useTranslation } from "react-i18next";
export const ScoreInput = ({ value, canEdit, placeholder, onSave }) => {
    const [localValue, setLocalValue] = useState(value ?? "");
    // Держим последнее value/onSave/localValue в ref только чтобы doFlush не
    // ловил устаревшее замыкание, если он вызывается не из последнего рендера
    // (в частности - из cleanup эффекта при unmount).
    const latestRef = useRef({ value, onSave, localValue });
    latestRef.current = { value, onSave, localValue };
    useEffect(() => {
        setLocalValue(value ?? "");
    }, [value]);
    const doFlush = (valToSave) => {
        const { value: propValue, onSave: save } = latestRef.current;
        const parsed = valToSave !== "" ? Number(valToSave) : null;
        if (parsed !== (propValue ?? null)) {
            save(parsed);
        }
    };
    const handleChange = (e) => {
        const val = e.target.value;
        setLocalValue(val);
        // Как в старой модалке: шлём на сервер сразу по onChange, без
        // debounce - очередь запросов на один матч сериализуется в
        // useMatchSync, так что параллельные вызовы не гонятся друг с другом.
        doFlush(val);
    };
    useEffect(() => {
        // Если компонент размонтируется (переключение вкладки/этапа/формата
        // сетки) до того, как последний ввод долетел до сервера - досылаем
        // его, иначе значение молча теряется.
        return () => {
            doFlush(latestRef.current.localValue);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (
        <input
            type="number"
            disabled={!canEdit}
            value={localValue}
            placeholder={placeholder}
            style={{ width: 40, textAlign: "center", zIndex: 1 }}
            onChange={handleChange}
            onBlur={() => doFlush(localValue)}
            onKeyDown={(e) => e.key === 'Enter' && doFlush(localValue)}
            onWheel={(e) => e.target.blur()}
        />
    );
};
// Set 1-5 switcher - a plain flex item, meant to sit as a normal sibling of
// the match-card's own title (group/round/stage label) inside THAT row, not
// floated over it - see TournamentGroupsMatchesModal.js/
// TournamentGridMatchesModal.js/TournamentScheduleMatchesModal.js's own
// "team-name-row" for where this gets rendered. activeSetIndex/
// setActiveSetIndex live in the parent modal (keyed per match, alongside its
// `scores` state) rather than here, precisely so the title row and
// MatchScoreColumns below both read/drive the same value.
export const SetSwitcher = ({ activeSetIndex, setActiveSetIndex }) => {
    const { t } = useTranslation();

    const handleSetChange = (direction) => {
        setActiveSetIndex(prev => {
            if (direction === 'prev' && prev > 0) return prev - 1;
            if (direction === 'next' && prev < 4) return prev + 1;
            return prev;
        });
    };

    return (
        <div className="set-switcher" style={{ display: "flex", alignItems: "center", gap: 8, border: 0 }}>
            {/* padding:15px/margin:-15px enlarges the tap target without
                shifting layout - same trick as the winner-radio label in
                MatchScoreColumns below, since the bare 16px icon alone was a
                near-unusable hit area on a touch screen. */}
            <span
                style={{ padding: "15px", margin: "-15px", display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: activeSetIndex > 0 ? "pointer" : "not-allowed" }}
                onClick={(e) => { e.stopPropagation(); handleSetChange('prev'); }}
            >
                <FeatherIcon
                    icon="chevron-left"
                    size={16}
                    style={{ opacity: activeSetIndex > 0 ? 1 : 0.3 }}
                />
            </span>
            <span>{activeSetIndex + 1} {t("set")}</span>
            <span
                style={{ padding: "15px", margin: "-15px", display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: activeSetIndex < 4 ? "pointer" : "not-allowed" }}
                onClick={(e) => { e.stopPropagation(); handleSetChange('next'); }}
            >
                <FeatherIcon
                    icon="chevron-right"
                    size={16}
                    style={{ opacity: activeSetIndex < 4 ? 1 : 0.3 }}
                />
            </span>
        </div>
    );
};

export const MatchScoreColumns = ({ matchId, winnerSide, rows, canEdit, is_authorized, onSave, matchSets = [], activeSetIndex = 0 }) => {
    const { t } = useTranslation();

    // Score format is per-match, not dictated by the tournament/stage's
    // games_in_set setting - an admin can always flip through sets 1-5 via
    // SetSwitcher above and enter a score for any of them. Spectators get no
    // switcher (always see set 1), so whether THEY see a sets-won tally or a
    // single game score depends only on whether this particular match
    // actually has more than one set with a score in it.
    const hasMultipleSets = matchSets.filter(s => s && (s.game_score_1 != null || s.game_score_2 != null)).length > 1;

    return (
        <>
            <div>
                {rows.map((row, rowIndex) => (
                    // center, not flex-end: the label below enlarges its own
                    // touch target via padding:15px + margin:-15px (grows the
                    // tap area without shifting layout - the negative margin
                    // shrinks the flex item's own margin box back down by the
                    // same 15px). flex-end aligns that smaller margin box
                    // flush against the column's own edge, which leaves the
                    // padding box's 15px bleeding straight past it into the
                    // score column next door - on a narrow phone that
                    // overlapped the score input enough to eat its taps.
                    // Centering keeps the bleed symmetric and inside this
                    // column on both sides, while still sitting visibly
                    // right of the name instead of hugging it.
                    <div key={rowIndex} className="team-player-row" style={{ justifyContent: "center" }}>
                        {is_authorized ? (
                            <label style={{ padding: "15px", margin: "-15px", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", zIndex: 2 }}>
                                <input
                                    type="radio"
                                    name={`winner-${matchId || Math.random()}`}
                                    disabled={!canEdit}
                                    checked={winnerSide === row.side}
                                    onChange={() => {
                                        if (winnerSide !== row.side) {
                                            onSave({ winner_side: row.side });
                                        }
                                    }}
                                    style={{ margin: 0, cursor: "inherit" }}
                                />
                            </label>
                        ) : (
                            winnerSide === row.side && (
                                <FeatherIcon icon="check" style={{ width: "16px" }} />
                            )
                        )}
                    </div>
                ))}
            </div>
            <div>
                {rows.map((row, rowIndex) => {
                    const isWinner = winnerSide === row.side;
                    const isLoser = winnerSide !== null && winnerSide !== undefined && winnerSide !== row.side;
                    
                    const games = matchSets[activeSetIndex]?.[`game_score_${row.side}`] ?? (activeSetIndex === 0 ? row.games : undefined);
                    const tb = matchSets[activeSetIndex]?.[`tiebreak_score_${row.side}`] ?? (activeSetIndex === 0 ? row.tb : undefined);

                    return (
                        <div key={rowIndex} className="team-player-row" style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end" }}>
                            {
                                is_authorized ? (
                                    <>
                                        <ScoreInput
                                            canEdit={canEdit}
                                            value={games}
                                            onSave={(val) => onSave({ [`game_score_${row.side}`]: val }, activeSetIndex)}
                                        />
                                        <ScoreInput
                                            canEdit={canEdit}
                                            value={tb}
                                            placeholder={t("Tb")}
                                            onSave={(val) => onSave({ [`tiebreak_score_${row.side}`]: val }, activeSetIndex)}
                                        />
                                    </>
                                ) : (
                                    hasMultipleSets ? (
                                        matchSets.map((setObj, setIdx) => {
                                            const g = setObj?.[`game_score_${row.side}`];
                                            const tVal = setObj?.[`tiebreak_score_${row.side}`];
                                            if (g == null && tVal == null) return null;
                                            
                                            let isSetLoser = false;
                                            const oppSide = row.side === 1 ? 2 : 1;
                                            const gOpp = setObj?.[`game_score_${oppSide}`];
                                            if (g != null && gOpp != null) {
                                                if (g < gOpp) isSetLoser = true;
                                                else if (g === gOpp) {
                                                    const tOpp = setObj?.[`tiebreak_score_${oppSide}`];
                                                    if (tVal != null && tOpp != null && tVal < tOpp) {
                                                        isSetLoser = true;
                                                    }
                                                }
                                            }

                                            return (
                                                <div key={setIdx} className={`row-games ${isSetLoser ? "winner" : ""}`} style={{ width: "15px", textAlign: "center" }}>
                                                    {g}
                                                    {tVal != null && (
                                                        <sup className={`row-tb ${isSetLoser ? "winner" : ""}`}>
                                                            {tVal}
                                                        </sup>
                                                    )}
                                                </div>
                                            );
                                        })
                                    ) : (
                                        (games != null || tb != null) && (
                                            <div className={`row-games ${isLoser && "winner"}`}>
                                                {games}
                                                {tb != null && (
                                                    <sup className={`row-tb ${isLoser && "winner"}`}>
                                                        {tb}
                                                    </sup>
                                                )}
                                            </div>
                                        )
                                    )
                                )
                            }
                        </div>
                    );
                })}
            </div>
        </>
    );
};